import React, { useEffect, useMemo } from "react";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Tabs,
  Tag,
  FormInstance,
  Alert,
  Divider,
  message,
  AutoComplete,
  Upload,
} from "antd";
import dayjs from "dayjs";
import { frontDeskService } from "../../../services/adminService";
import { RadioCardGroup } from "./RadioCardGroup";
import { calculateDuration } from "../../../utils/duration";
import { Offer, GuestProfile, RoomPackage } from "../../../types";

interface CreateBookingModalProps {
  open: boolean;
  onCancel: () => void;
  form: FormInstance;
  activeTab: string;
  setActiveTab: (key: string) => void;
  onSubmit: (values: any) => void;
  loading: boolean;
  isCreateValid: boolean;
  roomTypes: any[];
  roomAvailabilities: Record<string, any>;
  blockedDates: string[];
  createCheckInDate: dayjs.Dayjs | undefined;
  createCheckOutDate: dayjs.Dayjs | undefined;
  roomsList: any[];
  offers: Offer[];
  rooms: any[];
  guestProfiles: GuestProfile[];
  roomPackages: RoomPackage[];
}

export const CreateBookingModal: React.FC<CreateBookingModalProps> = ({
  open,
  onCancel,
  form,
  activeTab,
  setActiveTab,
  onSubmit,
  loading,
  isCreateValid,
  roomTypes,
  roomAvailabilities,
  blockedDates,
  createCheckInDate,
  createCheckOutDate,
  roomsList,
  offers,
  rooms,
  guestProfiles,
  roomPackages,
}) => {
  const [generatingQuotation, setGeneratingQuotation] = React.useState(false);
  const idDocumentType = Form.useWatch("id_document_type", form);

  useEffect(() => {
    if (roomPackages && roomPackages.length > 0) {
      const current = form.getFieldValue("room_package_id");
      if (!current) {
        const ro = roomPackages.find((p) => p.code === "RO");
        if (ro) form.setFieldsValue({ room_package_id: ro.id });
      }
    }
  }, [roomPackages, form]);

  const getRemainingAvailabilityForRow = React.useCallback((index: number, roomTypeId: string) => {
    const baseAvail = roomAvailabilities[roomTypeId];
    if (!baseAvail || baseAvail.acCount === undefined) return baseAvail;
    
    const av = JSON.parse(JSON.stringify(baseAvail));

    for (let i = 0; i < roomsList.length; i++) {
       if (i === index) continue;
       const r = roomsList[i];
       if (r && r.room_type_id === roomTypeId && r.quantity) {
          let q = r.quantity;
          if (r.ac_status === 'ac') {
            if (q <= av.acCount) av.acCount -= q;
            else { q -= av.acCount; av.acCount = 0; av.bothCount -= q; }
          } else if (r.ac_status === 'non_ac') {
            if (q <= av.nonAcCount) av.nonAcCount -= q;
            else { q -= av.nonAcCount; av.nonAcCount = 0; av.bothCount -= q; }
          } else {
            if (q <= av.bothCount) av.bothCount -= q;
            else {
              q -= av.bothCount; av.bothCount = 0;
              if (q <= av.acCount) { av.acCount -= q; q = 0; }
              else { q -= av.acCount; av.acCount = 0; }
              if (q > 0) {
                if (q <= av.nonAcCount) { av.nonAcCount -= q; q = 0; }
                else { q -= av.nonAcCount; av.nonAcCount = 0; }
              }
            }
          }
          av.availableCount -= r.quantity;
       }
    }
    
    av.acCount = Math.max(0, av.acCount);
    av.nonAcCount = Math.max(0, av.nonAcCount);
    av.bothCount = Math.max(0, av.bothCount);
    av.availableCount = Math.max(0, av.availableCount);
    
    return av;
  }, [roomsList, roomAvailabilities]);

  const handleCreateNext = async () => {
    try {
      if (activeTab === "1") {
        await form.validateFields([
          "guest_name",
          "email",
          "phone",
          "address_line_1",
          "address_line_2",
          "id_document_type",
          "id_document_number",
        ]);
      } else if (activeTab === "2") {
        const roomsList = form.getFieldValue("rooms") || [];
        const roomPaths = roomsList.flatMap((_: any, index: number) => [
          ["rooms", index, "room_type_id"],
          ["rooms", index, "quantity"],
          ["rooms", index, "ac_status"],
          ["rooms", index, "extra_beds"],
          ["rooms", index, "number_of_guests"]
        ]);
        await form.validateFields([
          "reservation_source",
          "rooms",
          "check_in_date",
          "check_out_date",
          ...roomPaths
        ]);
      }
      setActiveTab(String(Number(activeTab) + 1));
    } catch (e) {
      // Validation failed
    }
  };

  const earlyCheckInFee = Form.useWatch("early_check_in_fee", form) || 0;
  const lateCheckOutFee = Form.useWatch("late_check_out_fee", form) || 0;

  const nights =
    createCheckInDate && createCheckOutDate
      ? Math.max(createCheckOutDate.diff(createCheckInDate, "days"), 0)
      : 0;

  const now = useMemo(() => new Date(), []);

  const getActiveOffer = useMemo(() => {
    return (roomTypeId: string, acStatus?: string): Offer | null => {
      const activeOffersForRoom = offers.filter(
        (o) =>
          o.room_type_id === roomTypeId &&
          o.is_active &&
          new Date(o.start_date) <= now &&
          new Date(o.end_date) >= now &&
          !o.promo_code
      );
      
      if (acStatus) {
        const exactMatch = activeOffersForRoom.find((o) => o.ac_status === acStatus);
        if (exactMatch) return exactMatch;
      }
      
      return activeOffersForRoom.find((o) => !o.ac_status || o.ac_status === 'any') || null;
    };
  }, [offers, now]);

  let roomTotal = 0;
  let extraBedsTotal = 0;
  let totalOriginalRoomPrice = 0;
  let hasAnyDiscount = false;

  const roomBreakdowns: Array<{
    roomTypeName: string;
    quantity: number;
    originalPrice: number;
    discountedPrice: number;
    offerTitle: string | null;
    nights: number;
  }> = [];

  roomsList.forEach((r) => {
    if (r.room_type_id && r.quantity) {
      const roomType = roomTypes.find((rt) => rt.id === r.room_type_id);
      if (roomType) {
        let originalPrice = Number(roomType.base_price || 0);
        if (roomType.ac_status_preference === 'both') {
          if (r.ac_status === 'ac') originalPrice = Number(roomType.ac_base_price || roomType.base_price);
          else if (r.ac_status === 'non_ac') originalPrice = Number(roomType.non_ac_base_price || roomType.base_price);
        }
        let basePricePerNight = originalPrice;
        let offerTitle: string | null = null;

        const activeOffer = getActiveOffer(r.room_type_id, r.ac_status);
        if (activeOffer) {
          if (activeOffer.discount_amount) {
            basePricePerNight = Math.max(
              0,
              basePricePerNight - Number(activeOffer.discount_amount),
            );
          } else if (activeOffer.discount_percentage) {
            basePricePerNight = Math.round(
              basePricePerNight *
                (1 - Number(activeOffer.discount_percentage) / 100),
            );
          }
          offerTitle = activeOffer.title;
          hasAnyDiscount = true;
        }

        totalOriginalRoomPrice += originalPrice * Number(r.quantity) * nights;
        roomTotal += basePricePerNight * Number(r.quantity) * nights;

        const displayRoomName = roomType.name + (r.ac_status && roomType.ac_status_preference === 'both' ? ` (${r.ac_status === 'ac' ? 'AC' : 'Non-AC'})` : '');

        roomBreakdowns.push({
          roomTypeName: displayRoomName,
          quantity: Number(r.quantity),
          originalPrice,
          discountedPrice: basePricePerNight,
          offerTitle,
          nights,
        });

        if (r.extra_beds) {
          extraBedsTotal +=
            Number(roomType.extra_bed_price || 0) *
            Number(r.extra_beds) *
            nights;
        }
      }
    }
  });

  const discountType = Form.useWatch("discount_type", form) || "none";
  const discountValue = Form.useWatch("discount_value", form) || 0;
  const enableVat = Form.useWatch("enable_vat", form) || "no";
  const vatPercentage = Form.useWatch("vat_percentage", form) || 0;

  const serviceChargeType = Form.useWatch("service_charge_type", form) || "none";
  const serviceChargeValue = Form.useWatch("service_charge_value", form) || 0;

  const selectedPackageId = Form.useWatch("room_package_id", form);
  const selectedPackage = (roomPackages || []).find(
    (p) => p.id === selectedPackageId,
  );
  const totalGuests = roomsList.reduce(
    (sum, r) => sum + Number(r?.number_of_guests || 1) * Number(r?.quantity || 1),
    0,
  );
  const packageCharge = useMemo(() => {
    if (!selectedPackage) return 0;
    const base = Number(selectedPackage.base_price || 0);
    const perPerson = Number(selectedPackage.price_per_person || 0);
    const g = Math.max(totalGuests || 1, 1);
    const n = Math.max(nights || 1, 1);
    switch (selectedPackage.charge_basis) {
      case "per_room_per_night":
        return base * n;
      case "per_stay":
        return base;
      case "per_person_per_stay":
        return perPerson * g;
      case "per_person_per_night":
        return perPerson * g * n;
      case "complimentary":
      default:
        return 0;
    }
  }, [selectedPackage, totalGuests, nights]);

  const roomSubtotal = roomTotal + extraBedsTotal;
  const total1 =
    roomSubtotal + packageCharge + Number(earlyCheckInFee) + Number(lateCheckOutFee);

  let manualDiscountDeduction = 0;
  if (discountType === "fixed" && Number(discountValue) > 0) {
    manualDiscountDeduction = Number(discountValue);
  } else if (discountType === "percentage" && Number(discountValue) > 0) {
    manualDiscountDeduction = Math.round(total1 * Number(discountValue) / 100);
  }

  const total2 = Math.max(0, total1 - manualDiscountDeduction);

  let serviceCharge = 0;
  if (serviceChargeType === "fixed" && Number(serviceChargeValue) > 0) {
    serviceCharge = Number(serviceChargeValue);
  } else if (serviceChargeType === "percentage" && Number(serviceChargeValue) > 0) {
    serviceCharge = Math.round(total2 * Number(serviceChargeValue) / 100);
  }

  const total3 = total2 + serviceCharge;

  const taxValue = enableVat === "yes" ? Math.round(total3 * (Number(vatPercentage) / 100)) : 0;
  const finalTotal = total3 + taxValue;

  const depositInput = Form.useWatch("deposit_amount_input", form);
  const depositPaid = (depositInput !== undefined && depositInput !== null && depositInput !== "")
    ? Number(depositInput)
    : 0;

  const handleGenerateQuotation = async () => {
    try {
      setGeneratingQuotation(true);
      const values = await form.validateFields();
      
      const checkInFormatted = values.check_in_date.format("YYYY-MM-DD");
      const checkOutFormatted = values.check_out_date.format("YYYY-MM-DD");

      const enrichedRooms = (values.rooms || []).map((r: any) => {
        const roomType = roomTypes.find((rt) => rt.id === r.room_type_id);
        let originalPrice = Number(roomType?.base_price || 0);
        if (roomType?.ac_status_preference === 'both') {
          if (r.ac_status === 'ac') originalPrice = Number(roomType.ac_base_price || roomType.base_price);
          else if (r.ac_status === 'non_ac') originalPrice = Number(roomType.non_ac_base_price || roomType.base_price);
        }
        let discountedPrice = originalPrice;
        let offerTitle: string | null = null;

        const activeOffer = getActiveOffer(r.room_type_id, r.ac_status);

        if (activeOffer) {
          if (activeOffer.discount_amount) {
            discountedPrice = Math.max(0, discountedPrice - Number(activeOffer.discount_amount));
          } else if (activeOffer.discount_percentage) {
            discountedPrice = Math.round(discountedPrice * (1 - Number(activeOffer.discount_percentage) / 100));
          }
          offerTitle = activeOffer.title;
        }

        return {
          ...r,
          original_room_price: originalPrice,
          discounted_price: discountedPrice,
          offer_title: offerTitle,
        };
      });

      const payload: any = {
        ...values,
        check_in_date: checkInFormatted,
        check_out_date: checkOutFormatted,
        discount_amount: discountType === "fixed" ? Number(discountValue) : 0,
        discount_percentage: discountType === "percentage" ? Number(discountValue) : 0,
        vat_rate: enableVat === "yes" ? Number(vatPercentage) : 0,
        service_charge: Number(serviceCharge),
        package_charge: packageCharge,
        package_name: selectedPackage?.name || null,
        rooms: enrichedRooms,
        deposit_amount: depositPaid,
      };
      delete payload.discount_type;
      delete payload.discount_value;
      delete payload.enable_vat;
      delete payload.vat_percentage;

      const response: any = await frontDeskService.generateQuotation(payload);
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      
      message.success("Quotation generated successfully");
    } catch (error) {
      console.error(error);
      message.error("Failed to generate quotation or missing required fields");
    } finally {
      setGeneratingQuotation(false);
    }
  };

  const guestOptions = useMemo(() => {
    const uniqueMap = new Map();
    guestProfiles.forEach((guest) => {
      const key = guest.email?.toLowerCase().trim() || 
                  guest.phone?.trim() || 
                  guest.full_name?.toLowerCase().trim();
      
      if (key && !uniqueMap.has(key)) {
        uniqueMap.set(key, guest);
      }
    });

    return Array.from(uniqueMap.values()).map((guest) => ({
      value: guest.full_name,
      key: guest.id || guest.email || guest.full_name + Math.random(),
      label: (
        <div style={{ display: "flex", flexDirection: "column", padding: "4px 0" }}>
          <strong style={{ lineHeight: "1.2" }}>{guest.full_name}</strong>
          <span style={{ fontSize: "12px", color: "#888", lineHeight: "1.2", marginTop: "2px" }}>
            {guest.email} {guest.phone ? `• ${guest.phone}` : ""}
          </span>
        </div>
      ),
      guest,
    }));
  }, [guestProfiles]);

  return (
    <Modal
      open={open}
      title="Create Front Desk Booking"
      onCancel={onCancel}
      width={760}
      footer={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            onClick={() => {
              form.resetFields();
              form.setFieldsValue({
                rooms: [
                  { room_type_id: undefined, quantity: 1, extra_beds: 0 },
                ],
              });
              setActiveTab("1");
            }}
          >
            Reset
          </Button>
          <Space>
            {activeTab !== "1" && (
              <Button
                onClick={() => setActiveTab(String(Number(activeTab) - 1))}
              >
                Back
              </Button>
            )}
            {activeTab !== "3" && (
              <Button id="create-next-btn" type="primary" onClick={handleCreateNext}>
                Next
              </Button>
            )}
            {activeTab === "3" && (
              <>
                <Button
                  type="default"
                  loading={generatingQuotation}
                  onClick={handleGenerateQuotation}
                  disabled={!isCreateValid}
                >
                  Generate Quotation
                </Button>
                <Button
                  id="create-submit-btn"
                  type="primary"
                  loading={loading}
                  onClick={() => form.submit()}
                  disabled={!isCreateValid}
                >
                  Create Booking
                </Button>
              </>
            )}
          </Space>
        </div>
      }
    >
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Tabs
          activeKey={activeTab}
          onTabClick={(key) => {
            if (key !== activeTab) {
              const modalBody = document.querySelector('.ant-modal-body');
              if (modalBody) {
                modalBody.scrollTo({ top: modalBody.scrollHeight, behavior: 'smooth' });
              }
              const nextBtn = document.getElementById(activeTab === "3" ? 'create-submit-btn' : 'create-next-btn');
              if (nextBtn) {
                nextBtn.focus();
                nextBtn.style.boxShadow = '0 0 0 4px rgba(24, 144, 255, 0.4)';
                nextBtn.style.transition = 'box-shadow 0.3s ease';
                setTimeout(() => {
                  nextBtn.style.boxShadow = '';
                }, 1000);
              }
            }
          }}
          items={[
            {
              key: "1",
              label: "1. Guest Details",
              children: (
                <>
                  <div className="grid-two">
                  <Form.Item
                    name="guest_name"
                    label="Guest Name"
                    rules={[{ required: true }]}
                  >
                    <AutoComplete
                      options={guestOptions}
                      filterOption={(inputValue, option) =>
                        option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                      }
                      onSelect={(value, option) => {
                        const g = (option as any).guest;
                        if (g) {
                          setTimeout(() => {
                            const updates: any = {};
                            if (g.email) updates.email = g.email;
                            if (g.phone) updates.phone = g.phone;
                            if (g.id_document_type) updates.id_document_type = g.id_document_type;
                            if (g.id_document_number) updates.id_document_number = g.id_document_number;
                            if ((g as any).address_line_1) updates.address_line_1 = (g as any).address_line_1;
                            if ((g as any).address_line_2) updates.address_line_2 = (g as any).address_line_2;
                            if (g.preferences) updates.special_requests = g.preferences;
                            if (g.notes) updates.allocation_note = g.notes;
                            
                            form.setFieldsValue(updates);
                          }, 0);
                        }
                      }}
                      placeholder="Type guest name"
                    >
                      <Input />
                    </AutoComplete>
                  </Form.Item>
                  <Form.Item
                    name="email"
                    label="Email (optional)"
                    rules={[{ type: "email" }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="address_line_1" label="Address Line 1 (optional)">
                    <Input />
                  </Form.Item>
                  <Form.Item name="address_line_2" label="Address Line 2 (optional)">
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name="id_document_type"
                    label="ID Type (optional)"
                    style={{ gridColumn: "1 / -1" }}
                  >
                    <RadioCardGroup
                      options={[
                        { label: "Passport", value: "passport" },
                        { label: "National ID", value: "national_id" },
                        {
                          label: "Driving License",
                          value: "driving_license",
                        },
                      ]}
                    />
                  </Form.Item>
                  {idDocumentType && (
                    <Form.Item
                      name="id_document_number"
                      label={`${idDocumentType === "passport" ? "Passport" : idDocumentType === "national_id" ? "National ID" : "Driving License"} Number (optional)`}
                      style={{ gridColumn: "1 / -1" }}
                    >
                      <Input />
                    </Form.Item>
                  )}
                  <Form.Item
                    name="id_document_url"
                    label="ID Document Image (optional)"
                    style={{ gridColumn: "1 / -1" }}
                    valuePropName="file"
                    getValueFromEvent={(e) => {
                      if (e.file.status === "done" && e.file.response) {
                        return e.file.response.data?.url || e.file.response.url || "";
                      }
                      return "";
                    }}
                  >
                    <Upload
                      listType="picture-card"
                      maxCount={1}
                      customRequest={async ({ file, onSuccess, onError }) => {
                        try {
                          const result = await frontDeskService.uploadIdDocument(file as File);
                          onSuccess?.(result);
                        } catch (error) {
                          onError?.(error as Error);
                          message.error("Failed to upload document");
                        }
                      }}
                    >
                      <div>
                        <PlusOutlined />
                        <div style={{ marginTop: 8 }}>Upload</div>
                      </div>
                    </Upload>
                  </Form.Item>
                </div>
                </>
              ),
            },
            {
              key: "2",
              label: "2. Room & Dates",
              children: (
                <div className="grid-two">
                  <Form.Item
                    name="reservation_source"
                    label="Reservation Source"
                    initialValue="walk_in"
                    rules={[{ required: true }]}
                    style={{ gridColumn: "1 / -1" }}
                  >
                    <RadioCardGroup
                      options={[
                        { label: "Walk-In", value: "walk_in" },
                        { label: "Direct", value: "direct" },
                        { label: "Website", value: "website" },
                        { label: "Booking.com", value: "booking_com" },
                        { label: "Agoda", value: "agoda" },
                        { label: "Expedia", value: "expedia" },
                        { label: "Travel Agent", value: "travel_agent" },
                        { label: "Phone", value: "phone" },
                        { label: "WhatsApp", value: "whatsapp" },
                        { label: "Other", value: "other" },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item
                    name="check_in_date"
                    label="Check-In Date"
                    rules={[{ required: true }]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      disabledDate={(currentDate) => {
                        if (
                          currentDate &&
                          blockedDates.includes(
                            currentDate.format("YYYY-MM-DD"),
                          )
                        ) {
                          return true;
                        }
                        return false;
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="check_out_date"
                    label="Check-Out Date"
                    rules={[{ required: true }]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      disabledDate={(currentDate) => {
                        const checkInVal = form.getFieldValue("check_in_date");
                        if (
                          checkInVal &&
                          currentDate &&
                          currentDate.isBefore(
                            dayjs(checkInVal).add(1, "day").startOf("day"),
                          )
                        ) {
                          return true;
                        }
                        if (
                          currentDate &&
                          blockedDates.includes(
                            currentDate.format("YYYY-MM-DD"),
                          )
                        ) {
                          return true;
                        }
                        return false;
                      }}
                    />
                  </Form.Item>

                  <div style={{ gridColumn: "1 / -1" }}>
                    {!(createCheckInDate && createCheckOutDate) && (
                      <Alert
                        message="Please select the dates to select rooms"
                        type="info"
                        showIcon
                        style={{ marginBottom: "16px" }}
                      />
                    )}
                    <Form.List
                      name="rooms"
                      initialValue={[
                        { room_type_id: undefined, quantity: 1, extra_beds: 0 },
                      ]}
                    >
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name, ...restField }, index) => (
                            <div
                              key={key}
                              style={{
                                display: "flex",
                                gap: "16px",
                                alignItems: "flex-start",
                                marginBottom: "16px",
                                background: "#f9f9f9",
                                padding: "16px",
                                borderRadius: "8px",
                                flexWrap: "wrap",
                                opacity: !(
                                  createCheckInDate && createCheckOutDate
                                )
                                  ? 0.6
                                  : 1,
                              }}
                            >
                              <div style={{ flex: 1, minWidth: "200px" }}>
                                <Form.Item
                                  {...restField}
                                  name={[name, "room_type_id"]}
                                  label={`Room ${index + 1} Type`}
                                  rules={[
                                    {
                                      required: true,
                                      message: "Missing room type",
                                    },
                                  ]}
                                  style={{ marginBottom: "12px" }}
                                >
                                  <Select
                                    placeholder="Select a room type"
                                    disabled={
                                      !(createCheckInDate && createCheckOutDate)
                                    }
                                  >
                                    {roomTypes.map((rt) => (
                                      <Select.Option key={rt.id} value={rt.id}>
                                        {rt.name} (Max {rt.max_occupancy})
                                      </Select.Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                                {roomsList[index]?.room_type_id &&
                                  getRemainingAvailabilityForRow(index, roomsList[index].room_type_id) && (
                                    <div style={{ marginBottom: "12px" }}>
                                      {(() => {
                                        const av = getRemainingAvailabilityForRow(index, roomsList[index].room_type_id);
                                        const isAvail = av.isAvailable && av.availableCount >= (roomsList[index].quantity || 1);
                                        let text = "Fully Booked for Selected Dates";
                                        if (av.isAvailable) {
                                          if (av.acCount !== undefined) {
                                            const parts = [];
                                            if (av.acCount > 0) parts.push(`${av.acCount} AC`);
                                            if (av.nonAcCount > 0) parts.push(`${av.nonAcCount} Non-AC`);
                                            if (av.bothCount > 0) parts.push(`${av.bothCount} Any`);
                                            if (parts.length > 0) text = `Available: ${parts.join(', ')}`;
                                          } else {
                                            text = `Available: ${av.availableCount} free`;
                                          }
                                        }
                                        return <Tag color={isAvail ? "green" : "red"}>{text}</Tag>;
                                      })()}
                                    </div>
                                  )}

                                {createCheckInDate &&
                                  createCheckOutDate &&
                                  !roomsList[index]?.room_type_id && (
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#888",
                                        marginBottom: "8px",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      * Select a room type to enter quantity and
                                      extra beds
                                    </div>
                                  )}

                                <div className="grid-three" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" }}>
                                  <Form.Item
                                    {...restField}
                                    name={[name, "quantity"]}
                                    label="Quantity"
                                    rules={[
                                      { required: true, message: 'Required' },
                                      () => ({
                                        validator(_, value) {
                                          if (!value) return Promise.resolve();
                                          const r = roomsList[index];
                                          if (!r || !r.room_type_id) return Promise.resolve();
                                          
                                          const av = getRemainingAvailabilityForRow(index, r.room_type_id);
                                          if (!av) return Promise.resolve();
                                          if (!av.isAvailable || value > av.availableCount) {
                                            return Promise.reject(new Error(`Only ${av.availableCount} total available.`));
                                          }
                                          if (r.ac_status === 'ac' && av.acCount !== undefined && value > (av.acCount + (av.bothCount || 0))) {
                                            return Promise.reject(new Error(`Only ${av.acCount + (av.bothCount || 0)} AC available.`));
                                          }
                                          if (r.ac_status === 'non_ac' && av.nonAcCount !== undefined && value > (av.nonAcCount + (av.bothCount || 0))) {
                                            return Promise.reject(new Error(`Only ${av.nonAcCount + (av.bothCount || 0)} Non-AC available.`));
                                          }
                                          return Promise.resolve();
                                        }
                                      })
                                    ]}
                                    dependencies={[
                                      ['rooms', name, 'ac_status'],
                                      ['rooms', name, 'room_type_id']
                                    ]}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <InputNumber
                                      min={1}
                                      max={
                                        roomsList[index]?.room_type_id &&
                                        roomAvailabilities[
                                          roomsList[index].room_type_id
                                        ]?.isAvailable
                                          ? roomAvailabilities[
                                              roomsList[index].room_type_id
                                            ].availableCount
                                          : undefined
                                      }
                                      style={{ width: "100%" }}
                                      disabled={
                                        !(
                                          createCheckInDate &&
                                          createCheckOutDate
                                        ) || !roomsList[index]?.room_type_id
                                      }
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    {...restField}
                                    name={[name, "extra_beds"]}
                                    label="Extra Beds"
                                    style={{ marginBottom: 0 }}
                                  >
                                    <InputNumber
                                      min={0}
                                      style={{ width: "100%" }}
                                      disabled={
                                        !(
                                          createCheckInDate &&
                                          createCheckOutDate
                                        ) || !roomsList[index]?.room_type_id
                                      }
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    {...restField}
                                    name={[name, "number_of_guests"]}
                                    label="Total Guests"
                                    rules={[{ required: true }]}
                                    style={{ marginBottom: 0 }}
                                    extra={
                                      roomsList[index]?.room_type_id && roomTypes.find((rt) => rt.id === roomsList[index].room_type_id)
                                        ? <Tag color="blue" style={{ marginTop: '8px' }}>Max Occupancy: {(roomTypes.find((rt) => rt.id === roomsList[index].room_type_id)?.max_occupancy || 1) * (roomsList[index]?.quantity || 1) + (roomsList[index]?.extra_beds || 0)}</Tag>
                                        : undefined
                                    }
                                  >
                                    <InputNumber
                                      min={1}
                                      max={
                                        roomsList[index]?.room_type_id && roomTypes.find((rt) => rt.id === roomsList[index].room_type_id)
                                          ? (roomTypes.find((rt) => rt.id === roomsList[index].room_type_id)?.max_occupancy || 1) * (roomsList[index]?.quantity || 1) + (roomsList[index]?.extra_beds || 0)
                                          : undefined
                                      }
                                      style={{ width: "100%" }}
                                      disabled={
                                        !(
                                          createCheckInDate &&
                                          createCheckOutDate
                                        ) || !roomsList[index]?.room_type_id
                                      }
                                    />
                                  </Form.Item>
                                </div>
                                {roomsList[index]?.room_type_id && (() => {
                                  const rtId = roomsList[index].room_type_id;
                                  const typeRooms = rooms.filter(r => r.room_type_id === rtId && r.status !== 'maintenance' && r.status !== 'out_of_order' && r.status !== 'out_of_service');
                                  const hasAC = typeRooms.some(r => r.ac_status === 'ac' || r.ac_status === 'both');
                                  const hasNonAC = typeRooms.some(r => r.ac_status === 'non_ac' || r.ac_status === 'both');
                                  
                                  let acOptions: any[] = [];
                                  if (hasAC && hasNonAC) {
                                    acOptions = [
                                      { label: "AC", value: "ac" },
                                      { label: "Non-AC", value: "non_ac" }
                                    ];
                                  } else if (hasAC) {
                                    acOptions = [{ label: "AC Only", value: "ac" }];
                                    if (roomsList[index].ac_status !== 'ac') {
                                      setTimeout(() => {
                                        const fields = form.getFieldValue('rooms') || [];
                                        fields[index] = { ...fields[index], ac_status: 'ac' };
                                        form.setFieldsValue({ rooms: fields });
                                      }, 0);
                                    }
                                  } else if (hasNonAC) {
                                    acOptions = [{ label: "Non-AC Only", value: "non_ac" }];
                                    if (roomsList[index].ac_status !== 'non_ac') {
                                      setTimeout(() => {
                                        const fields = form.getFieldValue('rooms') || [];
                                        fields[index] = { ...fields[index], ac_status: 'non_ac' };
                                        form.setFieldsValue({ rooms: fields });
                                      }, 0);
                                    }
                                  }

                                  if (acOptions.length > 0) {
                                    return (
                                      <div style={{ marginTop: '16px' }}>
                                        <Form.Item
                                          {...restField}
                                          name={[name, "ac_status"]}
                                          label="AC / Non-AC Preference"
                                          rules={[{ required: true, message: 'Please select AC preference' }]}
                                          style={{ marginBottom: 0 }}
                                        >
                                          <RadioCardGroup options={acOptions} />
                                        </Form.Item>
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                              {fields.length > 1 && (
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => remove(name)}
                                  style={{ marginTop: "32px" }}
                                  disabled={
                                    !(createCheckInDate && createCheckOutDate)
                                  }
                                />
                              )}
                            </div>
                          ))}
                          <Form.Item>
                            <Button
                              type="dashed"
                              onClick={() =>
                                add({ quantity: 1, extra_beds: 0 })
                              }
                              block
                              icon={<PlusOutlined />}
                              disabled={
                                !(createCheckInDate && createCheckOutDate)
                              }
                            >
                              Add Another Room
                            </Button>
                          </Form.Item>
                        </>
                      )}
                    </Form.List>
                  </div>
                  {createCheckInDate &&
                    createCheckOutDate &&
                    calculateDuration(
                      createCheckInDate,
                      createCheckOutDate,
                    ) && (
                      <div style={{ gridColumn: "1 / -1", textAlign: "left" }}>
                        <Tag
                          color="#B79A68"
                          style={{ fontSize: "14px", padding: "4px 8px" }}
                        >
                          Selected Duration:{" "}
                          {calculateDuration(
                            createCheckInDate,
                            createCheckOutDate,
                          )}
                        </Tag>
                      </div>
)}
                </div>
              ),
},
              {
                key: "3",
                label: "3. Other Details",
                children: (
                  <>
                    <Form.Item
                      name="room_package_id"
                      label="Room Package"
                      style={{ marginBottom: "12px" }}
                    >
                      <Select
                        placeholder="Select a package"
                        options={(roomPackages || []).map((p) => ({
                          label: `${p.name} (${p.code})`,
                          value: p.id,
                        }))}
                      />
                    </Form.Item>
                    <div className="grid-two">
                    <Form.Item
                      name="early_check_in_fee"
                      label="Early Check-In Fee (optional)"
                    >
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item
                      name="late_check_out_fee"
                      label="Late Check-Out Fee (optional)"
                    >
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </div>
                  <div className="grid-two">
                    <Form.Item
                      name="late_arrival"
                      valuePropName="checked"
                    >
                      <Checkbox>Late Arrival</Checkbox>
                    </Form.Item>
                    <Form.Item
                      name="airport_pickup"
                      valuePropName="checked"
                    >
                      <Checkbox>Airport Pickup</Checkbox>
                    </Form.Item>
                  </div>
                  <Form.Item
                    noStyle
                    shouldUpdate={(prev, cur) => prev.airport_pickup !== cur.airport_pickup}
                  >
                    {({ getFieldValue }) =>
                      getFieldValue("airport_pickup") && (
                        <Form.Item
                          name="airport_pickup_time"
                          label="Pickup Time"
                        >
                          <Select placeholder="Select time">
                            {Array.from({ length: 36 }, (_, i) => {
                              const h = Math.floor(i / 2) + 6;
                              const m = i % 2 === 0 ? "00" : "30";
                              if (h > 23) return null;
                              const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
                              const ampm = h < 12 ? "AM" : "PM";
                              const value = `${String(h).padStart(2, "0")}:${m}`;
                              const label = `${String(h12).padStart(2, "0")}:${m} ${ampm}`;
                              return <Select.Option key={value} value={value}>{label}</Select.Option>;
                            }).filter(Boolean)}
                          </Select>
                        </Form.Item>
                      )
                    }
                  </Form.Item>
                  <Form.Item
                    name="special_requests"
                    label="Special Requests (optional)"
                  >
                    <Input.TextArea rows={3} />
                  </Form.Item>
                  <Form.Item
                    name="allocation_note"
                    label="Allocation Note (optional)"
                  >
                    <Input.TextArea rows={2} />
                  </Form.Item>

                  <Divider style={{ margin: "12px 0" }} />
                  <h4 style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#555" }}>
                    Manual Discount (optional)
                  </h4>
                  <div className="grid-two">
                    <Form.Item
                      name="discount_type"
                      label="Discount Type"
                      initialValue="none"
                    >
                      <RadioCardGroup
                        options={[
                          { label: "None", value: "none" },
                          { label: "Fixed (LKR)", value: "fixed" },
                          { label: "Percentage (%)", value: "percentage" },
                        ]}
                      />
                    </Form.Item>
                    {discountType !== "none" && (
                      <Form.Item
                        name="discount_value"
                        label={
                          discountType === "fixed"
                            ? "Discount Amount (LKR)"
                            : "Discount Percentage (%)"
                        }
                      >
                        <InputNumber
                          min={0}
                          max={discountType === "percentage" ? 100 : undefined}
                          style={{ width: "100%" }}
                          placeholder={
                            discountType === "fixed" ? "e.g. 2000" : "e.g. 10"
                          }
                        />
                      </Form.Item>
                    )}
                  </div>

                  <Divider style={{ margin: "12px 0" }} />
                  <h4 style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#555" }}>
                    VAT Setup (optional)
                  </h4>
                  <div className="grid-two">
                    <Form.Item
                      name="enable_vat"
                      label="Apply VAT?"
                      initialValue="no"
                    >
                      <RadioCardGroup
                        options={[
                          { label: "No VAT", value: "no" },
                          { label: "Enable VAT", value: "yes" },
                        ]}
                      />
                    </Form.Item>
                    {enableVat === "yes" && (
                      <Form.Item
                        name="vat_percentage"
                        label="VAT Percentage (%)"
                        rules={[{ required: true, message: "Please enter VAT percentage" }]}
                      >
                        <InputNumber
                          min={0}
                          max={100}
                          style={{ width: "100%" }}
                          placeholder="e.g. 18"
                        />
                      </Form.Item>
                    )}
                  </div>

                  <Divider style={{ margin: "12px 0" }} />
                  <h4 style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#555" }}>
                    Service Charge (optional)
                  </h4>
                  <div className="grid-two">
                    <Form.Item
                      name="service_charge_type"
                      label="Service Charge Type"
                      initialValue="none"
                    >
                      <RadioCardGroup
                        options={[
                          { label: "None", value: "none" },
                          { label: "Fixed (LKR)", value: "fixed" },
                          { label: "Percentage (%)", value: "percentage" },
                        ]}
                      />
                    </Form.Item>
                    {serviceChargeType !== "none" && (
                      <Form.Item
                        name="service_charge_value"
                        label={
                          serviceChargeType === "fixed"
                            ? "Service Charge Amount (LKR)"
                            : "Service Charge Percentage (%)"
                        }
                      >
                        <InputNumber
                          min={0}
                          max={serviceChargeType === "percentage" ? 100 : undefined}
                          style={{ width: "100%" }}
                          placeholder={
                            serviceChargeType === "fixed" ? "e.g. 500" : "e.g. 10"
                          }
                        />
                      </Form.Item>
                    )}
                  </div>

                  <Divider style={{ margin: "12px 0" }} />
                  <h4 style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#555" }}>
                    Deposit Amount (Optional)
                  </h4>
                  <Form.Item
                    name="deposit_amount_input"
                    style={{ marginBottom: "4px" }}
                  >
                    <InputNumber
                      min={0}
                      max={finalTotal}
                      style={{ width: "100%" }}
                      placeholder={Math.round(finalTotal * 0.3).toLocaleString()}
                      formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      parser={(value) => Number(value?.replace(/,/g, '') || 0) as any}
                      addonAfter="LKR"
                    />
                  </Form.Item>
                  <div style={{ fontSize: "12px", color: "#888", marginBottom: "12px" }}>
                    Leave blank or 0 if no deposit is required. Suggested 30%: LKR {Math.round(finalTotal * 0.3).toLocaleString()}
                  </div>

                  {nights > 0 && finalTotal > 0 && (
                    <div
                      style={{
                        marginTop: "24px",
                        padding: "16px",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "8px",
                      }}
                    >
                      <h3 style={{ margin: "0 0 16px 0", fontSize: "16px" }}>
                        Price Breakdown
                      </h3>
                      {roomBreakdowns.map((rb, idx) => (
                        <div
                          key={idx}
                          style={{
                            marginBottom: "12px",
                            padding: "10px 12px",
                            backgroundColor: "#fff",
                            borderRadius: "6px",
                            border: "1px solid #e8e8e8",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "6px",
                            }}
                          >
                            <span style={{ fontWeight: 600, fontSize: "14px" }}>
                              {rb.roomTypeName}
                              {rb.offerTitle && (
                                <Tag
                                  color="green"
                                  style={{
                                    marginLeft: "8px",
                                    fontSize: "11px",
                                  }}
                                >
                                  {rb.offerTitle}
                                </Tag>
                              )}
                            </span>
                            <span style={{ fontWeight: 600, fontSize: "14px" }}>
                              LKR{" "}
                              {(rb.discountedPrice * rb.quantity * rb.nights).toLocaleString(
                                "en-US",
                                { minimumFractionDigits: 2 },
                              )}
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#888",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span>
                              {rb.offerTitle ? (
                                <>
                                  <span
                                    style={{
                                      textDecoration: "line-through",
                                      color: "#bbb",
                                      marginRight: "4px",
                                    }}
                                  >
                                    LKR{" "}
                                    {rb.originalPrice.toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                    })}
                                  </span>
                                  <span style={{ color: "#389e0d", fontWeight: 500 }}>
                                    LKR{" "}
                                    {rb.discountedPrice.toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                    })}
                                  </span>
                                  {" / night"}
                                </>
                              ) : (
                                <>
                                  LKR{" "}
                                  {rb.discountedPrice.toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                  })}
                                  {" / night"}
                                </>
                              )}
                            </span>
                            <span style={{ color: "#666" }}>
                              {rb.quantity} {rb.quantity === 1 ? "room" : "rooms"} × {rb.nights}{" "}
                              {rb.nights === 1 ? "night" : "nights"}
                            </span>
                          </div>
                        </div>
                      ))}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "8px",
                        }}
                      >
                        <span>
                          Room Subtotal
                        </span>
                        <span>
                          LKR{" "}
                          {roomTotal.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                      {selectedPackage && packageCharge > 0 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                          }}
                        >
                          <span>Room Package ({selectedPackage.name})</span>
                          <span>
                            LKR{" "}
                            {packageCharge.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      )}
                      {extraBedsTotal > 0 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                          }}
                        >
                          <span>Extra Beds Charges</span>
                          <span>
                            LKR{" "}
                            {extraBedsTotal.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      )}
                      {Number(earlyCheckInFee) > 0 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                          }}
                        >
                          <span>Early Check-In Fee</span>
                          <span>
                            LKR{" "}
                            {Number(earlyCheckInFee).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      )}
                      {Number(lateCheckOutFee) > 0 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                          }}
                        >
                          <span>Late Check-Out Fee</span>
                          <span>
                            LKR{" "}
                            {Number(lateCheckOutFee).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      )}
                      {Number(serviceCharge) > 0 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                          }}
                        >
                          <span>Service Charge</span>
                          <span>
                            LKR{" "}
                            {Number(serviceCharge).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      )}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "8px",
                        }}
                      >
                        <span>Taxes & Fees ({enableVat === "yes" ? vatPercentage : 0}%)</span>
                        <span>
                          LKR{" "}
                          {taxValue.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                      {manualDiscountDeduction > 0 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                            color: "#389e0d",
                          }}
                        >
                          <span>
                            Manual Discount
                            {discountType === "percentage" && (
                              <span style={{ fontSize: "12px", color: "#888", marginLeft: "4px" }}>
                                ({discountValue}%)
                              </span>
                            )}
                          </span>
                          <span>
                            − LKR{" "}
                            {manualDiscountDeduction.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      )}
                      <Divider style={{ margin: "12px 0" }} />
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontWeight: "bold",
                          fontSize: "16px",
                          marginBottom: "8px",
                        }}
                      >
                        <span>Total Price</span>
                        <span>
                          LKR{" "}
                          {finalTotal.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "8px",
                          padding: "8px 12px",
                          backgroundColor: "#fff",
                          borderRadius: "6px",
                          border: "1px solid #e8e8e8",
                        }}
                      >
                        <span>Deposit</span>
                        <span style={{ fontWeight: 600 }}>
                          LKR{" "}
                          {depositPaid.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              ),
            },
          ]}
        />
      </Form>
    </Modal>
  );
};
